(() => {
class GlobalBottomSheet {
  constructor(root) {
    this.root = root;
    this.panel = root.querySelector('[data-global-bottom-sheet-panel]');
    this.dragZone = root.querySelector('[data-global-bottom-sheet-drag]');
    this.backdrop = root.querySelector('[data-global-bottom-sheet-backdrop]');
    this.owner = root.closest('details-modal');
    this.details = this.owner?.querySelector('details');
    this.pointerId = undefined;
    this.startY = 0;
    this.startHeight = 0;
    this.startTop = 0;
    this.startTime = 0;
    this.deltaY = 0;
    this.didDrag = false;
    this.suppressClick = false;
    this.closeTimer = undefined;
    this.viewportFrame = undefined;
    this.isPortaledSearch = false;

    if (!this.panel || !this.dragZone) return;

    this.summaryToggle = this.owner?.querySelector('summary');
    this.setupMobileSearchPortal();
    this.onViewportChange = this.onViewportChange.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.summaryToggle?.addEventListener('click', this.onSummaryToggle.bind(this), true);
    this.dragZone.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.dragZone.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.dragZone.addEventListener('pointerup', (event) => this.finishDrag(event));
    this.dragZone.addEventListener('pointercancel', (event) => this.finishDrag(event, true));
    this.dragZone.addEventListener('click', this.onHandleClick.bind(this), true);
    this.backdrop?.addEventListener('click', this.onBackdropClick.bind(this), true);

    if (this.isPortaledSearch) {
      this.root.addEventListener('focusin', this.onFocusIn);
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onViewportChange);
      window.addEventListener('orientationchange', this.onViewportChange);
    }

    if (this.details) {
      this.openObserver = new MutationObserver(() => {
        if (this.details.open) {
          this.reset();
        } else if (this.isPortaledSearch && this.root.classList.contains('is-open')) {
          this.root.classList.remove('is-open');
          this.root.hidden = true;
          this.portalHost?.classList.remove('has-input-focus');
          document.body.classList.remove('overflow-hidden');
        }
      });
      this.openObserver.observe(this.details, { attributes: true, attributeFilter: ['open'] });
    }
  }

  setupMobileSearchPortal() {
    if (!window.matchMedia('(max-width: 749px)').matches || !this.root.classList.contains('bambu-search')) return;

    this.portalPlaceholder = document.createComment('mobile-search-sheet-placeholder');
    this.root.parentNode.insertBefore(this.portalPlaceholder, this.root);
    this.portalHost = document.createElement('div');
    this.portalHost.className = 'bambu-search-modal bambu-search-portal';
    document.body.append(this.portalHost);
    this.portalHost.append(this.root);
    this.root.hidden = true;
    this.isPortaledSearch = true;
  }

  onSummaryToggle(event) {
    this.syncViewport();
    if (!this.isPortaledSearch) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.isOpen()) {
      this.close(true);
      return;
    }

    this.openPortaledSearch();
  }

  openPortaledSearch() {
    window.clearTimeout(this.closeTimer);
    this.root.classList.remove('is-open');
    this.reset();
    this.root.hidden = false;
    this.details?.setAttribute('open', '');
    this.summaryToggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');

    this.root.getBoundingClientRect();
    this.root.classList.add('is-open');
    this.panel.focus({ preventScroll: true });
  }

  isOpen() {
    if (this.details) return this.details.open;
    return this.root.classList.contains('is-open') && !this.root.hidden;
  }

  syncViewport() {
    const viewport = window.visualViewport;
    const viewportHeight = Math.floor(viewport?.height || window.innerHeight);
    const viewportTop = Math.max(0, Math.floor(viewport?.offsetTop || 0));
    this.root.style.setProperty('--bottom-sheet-viewport-height', `${viewportHeight}px`);
    this.root.style.setProperty('--bottom-sheet-viewport-top', `${viewportTop}px`);
  }

  onViewportChange() {
    if (!this.isOpen() || this.viewportFrame) return;

    this.viewportFrame = window.requestAnimationFrame(() => {
      this.viewportFrame = undefined;
      this.syncViewport();
    });
  }

  onFocusIn(event) {
    if (!event.target.matches('.bambu-search__input')) return;

    // A percentage-height bottom sheet becomes shorter when the mobile keyboard
    // reduces the visual viewport, which makes its top edge appear to drop.
    // Expand it before that resize and keep it pinned to the visible viewport.
    this.root.classList.add('has-input-focus');
    this.portalHost?.classList.add('has-input-focus');
    this.panel.classList.add('is-expanded', 'is-input-focused');
    this.panel.style.removeProperty('transition');
    this.panel.style.removeProperty('transform');
    this.panel.style.removeProperty('height');
    this.panel.style.removeProperty('border-radius');
    this.syncViewport();
  }

  reset() {
    window.clearTimeout(this.closeTimer);
    window.cancelAnimationFrame(this.viewportFrame);
    this.viewportFrame = undefined;
    this.syncViewport();
    this.root.classList.remove('has-input-focus');
    this.portalHost?.classList.remove('has-input-focus');
    this.panel.classList.remove('is-closing', 'is-dragging', 'is-expanded', 'is-input-focused');
    this.panel.style.removeProperty('transition');
    this.panel.style.removeProperty('transform');
    this.panel.style.removeProperty('height');
    this.panel.style.removeProperty('border-radius');
    this.backdrop?.style.removeProperty('opacity');
  }

  onPointerDown(event) {
    if (event.button !== 0 || !this.isOpen()) return;

    const panelRect = this.panel.getBoundingClientRect();
    this.startY = event.clientY;
    this.startHeight = panelRect.height;
    this.startTop = panelRect.top;
    this.startTime = performance.now();
    this.deltaY = 0;
    this.didDrag = false;
    this.pointerId = event.pointerId;
    this.dragZone.setPointerCapture(event.pointerId);
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;

    this.deltaY = event.clientY - this.startY;
    if (Math.abs(this.deltaY) < 3 && !this.didDrag) return;

    this.didDrag = true;
    event.preventDefault();
    this.panel.classList.add('is-dragging');

    if (this.deltaY >= 0) {
      const downwardDistance = this.deltaY;
      this.panel.style.transform = `translate3d(0, ${downwardDistance}px, 0)`;
      const progress = Math.min(downwardDistance / this.panel.offsetHeight, 1);
      if (this.backdrop) this.backdrop.style.opacity = String(Math.max(0, 1 - progress * 1.35));
      return;
    }

    if (this.panel.classList.contains('is-expanded')) return;

    const upwardDistance = Math.min(-this.deltaY, this.startTop);
    const expansionProgress = this.startTop ? Math.min(upwardDistance / this.startTop, 1) : 1;
    this.panel.style.transform = 'translate3d(0, 0, 0)';
    this.panel.style.height = `${Math.min(this.root.clientHeight, this.startHeight + upwardDistance)}px`;
    this.panel.style.borderRadius = `${26 * (1 - expansionProgress)}px ${26 * (1 - expansionProgress)}px 0 0`;
  }

  finishDrag(event, cancelled = false) {
    if (this.pointerId === undefined || event.pointerId !== this.pointerId) return;

    if (this.dragZone.hasPointerCapture(event.pointerId)) {
      this.dragZone.releasePointerCapture(event.pointerId);
    }

    const elapsed = Math.max(performance.now() - this.startTime, 1);
    const downwardDistance = Math.max(0, this.deltaY);
    const upwardDistance = Math.max(0, -this.deltaY);
    const dismissDistance = Math.min(140, this.panel.offsetHeight * 0.22);
    const expandDistance = Math.min(120, Math.max(48, this.startTop * 0.4));
    const shouldDismiss =
      !cancelled &&
      this.didDrag &&
      (downwardDistance >= dismissDistance || (downwardDistance > 24 && downwardDistance / elapsed > 0.65));
    const shouldExpand =
      !cancelled &&
      this.didDrag &&
      upwardDistance > 0 &&
      (upwardDistance >= expandDistance || (upwardDistance > 24 && upwardDistance / elapsed > 0.65));

    this.pointerId = undefined;
    if (!this.didDrag) return;

    this.suppressClick = true;
    window.setTimeout(() => {
      this.suppressClick = false;
    }, 0);

    if (shouldDismiss) {
      this.close(true);
    } else if (shouldExpand) {
      this.expand();
    } else {
      this.snapBack();
    }
  }

  expand() {
    this.panel.classList.remove('is-dragging');
    this.panel.classList.add('is-expanded');
    this.panel.style.transition =
      'height 380ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 380ms cubic-bezier(0.22, 1, 0.36, 1)';
    this.panel.style.height = '100%';
    this.panel.style.borderRadius = '0';
    this.backdrop?.style.removeProperty('opacity');

    window.setTimeout(() => {
      this.panel.style.removeProperty('transition');
      this.panel.style.removeProperty('height');
      this.panel.style.removeProperty('border-radius');
    }, 390);
  }

  snapBack() {
    this.panel.classList.remove('is-dragging');
    this.panel.style.transition =
      'transform 360ms cubic-bezier(0.22, 1, 0.36, 1), height 360ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 360ms cubic-bezier(0.22, 1, 0.36, 1)';
    this.panel.style.transform = 'translate3d(0, 0, 0)';
    if (!this.panel.classList.contains('is-expanded')) {
      this.panel.style.removeProperty('height');
      this.panel.style.removeProperty('border-radius');
    }
    this.backdrop?.style.removeProperty('opacity');

    window.setTimeout(() => {
      this.panel.classList.remove('is-dragging');
      this.panel.style.removeProperty('transition');
      this.panel.style.removeProperty('transform');
      this.panel.style.removeProperty('height');
      this.panel.style.removeProperty('border-radius');
    }, 370);
  }

  close(returnFocus = true) {
    if (!this.isOpen() || this.panel.classList.contains('is-closing')) return;

    this.panel.classList.remove('is-dragging');
    this.panel.classList.add('is-closing');
    this.panel.style.removeProperty('transform');
    this.panel.style.removeProperty('height');
    this.panel.style.removeProperty('border-radius');
    this.backdrop?.style.removeProperty('opacity');

    this.closeTimer = window.setTimeout(() => {
      this.panel.classList.remove('is-closing', 'is-expanded');
      this.panel.style.removeProperty('transition');
      this.panel.style.removeProperty('transform');
      if (this.isPortaledSearch) {
        this.root.classList.remove('is-open');
        this.root.hidden = true;
        this.portalHost?.classList.remove('has-input-focus');
        this.details?.removeAttribute('open');
        this.summaryToggle?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        if (returnFocus) this.summaryToggle?.focus({ preventScroll: true });
      } else {
        this.owner?.close(returnFocus);
      }
      this.root.dispatchEvent(new CustomEvent('global-bottom-sheet:closed', { bubbles: true }));
    }, 300);
  }

  onHandleClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.suppressClick) return;
    this.close(true);
  }

  onBackdropClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.close(false);
  }
}

const initializeGlobalBottomSheets = () => {
  document.querySelectorAll('[data-global-bottom-sheet]').forEach((root) => {
    if (root.globalBottomSheet) return;
    root.globalBottomSheet = new GlobalBottomSheet(root);
    root.setAttribute('data-global-bottom-sheet-ready', '');
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGlobalBottomSheets);
} else {
  initializeGlobalBottomSheets();
}
})();
